import { useAuthStore } from '@/store/useAuthStore';
import type { CopyDataJobSnapshot } from '@/types/copy-data-progress';

const COPY_DATA_LOG_PREFIX = '[CopyDataProgress]';

/** Timestamp của lần write gần nhất trên tab này — bỏ qua echo BroadcastChannel cùng tab. */
let lastLocalWriteTs = 0;

export function logCopyDataTrace(step: string, detail?: unknown): void {
  if (detail !== undefined) {
    console.info(`${COPY_DATA_LOG_PREFIX}[${step}]`, detail);
  } else {
    console.info(`${COPY_DATA_LOG_PREFIX}[${step}]`);
  }
}

export function getCopyDataSiteKey(): string {
  const tenantID = useAuthStore.getState().tenantID;
  const site = String(tenantID ?? '').trim();
  return site || 'unknown';
}

function copyDataJobKey(site = getCopyDataSiteKey()): string {
  return `${site}:copy-data-job`;
}

function copyDataChannelName(site = getCopyDataSiteKey()): string {
  return `${site}:copy-data-channel`;
}

export function writeCopyDataJobSnapshot(snapshot: CopyDataJobSnapshot): void {
  if (typeof window === 'undefined') return;

  const withTs: CopyDataJobSnapshot = { ...snapshot, t: Date.now() };
  lastLocalWriteTs = withTs.t;

  try {
    localStorage.setItem(copyDataJobKey(), JSON.stringify(withTs));
  } catch {
    // ignore
  }

  try {
    const channel = new BroadcastChannel(copyDataChannelName());
    channel.postMessage(withTs);
    channel.close();
  } catch {
    // ignore
  }
  // Không emit CustomEvent cùng tab — store đã cập nhật trực tiếp; tránh applyRemoteSnapshot echo.
}

export function readCopyDataJobSnapshot(): CopyDataJobSnapshot | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(copyDataJobKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CopyDataJobSnapshot;
    if (!parsed?.jobId || !parsed.phase) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearCopyDataJobSnapshot(): void {
  if (typeof window === 'undefined') return;

  lastLocalWriteTs = Date.now();

  try {
    localStorage.removeItem(copyDataJobKey());
  } catch {
    // ignore
  }

  try {
    const channel = new BroadcastChannel(copyDataChannelName());
    channel.postMessage(null);
    channel.close();
  } catch {
    // ignore
  }
}

export function clearCopyDataStorageOnLogout(): void {
  if (typeof window === 'undefined') return;

  try {
    clearCopyDataJobSnapshot();
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.endsWith(':copy-data-job')) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
    logCopyDataTrace('clear-on-logout', { removed: toRemove.length, site: getCopyDataSiteKey() });
  } catch {
    // ignore
  }
}

export function subscribeCopyDataJob(
  onChange: (snapshot: CopyDataJobSnapshot | null) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const storageKey = copyDataJobKey();
  const channelName = copyDataChannelName();

  // storage event: chỉ tab khác
  const onStorage = (e: StorageEvent) => {
    if (e.storageArea !== localStorage || e.key !== storageKey) return;
    if (!e.newValue) {
      onChange(null);
      return;
    }
    try {
      onChange(JSON.parse(e.newValue) as CopyDataJobSnapshot);
    } catch {
      // ignore
    }
  };

  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(channelName);
    channel.onmessage = (ev) => {
      const data = (ev.data as CopyDataJobSnapshot | null) ?? null;
      // Bỏ echo từ chính tab này (sender dùng channel khác nên vẫn nhận được message)
      if (data && data.t === lastLocalWriteTs) return;
      onChange(data);
    };
  } catch {
    channel = null;
  }

  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener('storage', onStorage);
    try {
      channel?.close();
    } catch {
      // ignore
    }
  };
}
